import TreasuryTable from './TreasuryTable';
import TreasuryDialog from './TreasuryDialog';
import './QuoteCard.css';
import { useEffect, useRef, useState } from 'react';
import './QuotesApp.css';
import UserInfo from '../login/UserInfo';
import HeaderTabs from '../interface/HeaderTabs';
import Pressable from '../other/Pressable';
import { fetchAPI, fetchAPIPromise, postAPI, deleteAPI, putAPI } from '../../utils/utils';
import useScheduler from '../forms/utils/useScheduler.js';
import QuoteRevenueChart from './QuoteRevenueChart';
import QuoteReminderModal from './QuoteReminderModal';
import { can } from '../../utils/permissions';
import CreateQuoteForm from './CreateQuoteForm';
import StripeConnectTab from './StripeConnectTab';
import QuoteVariantsEditor from './QuoteVariantsEditor';
import SubscriptionsTab from './SubscriptionsTab';
import SponsorsTab from './SponsorsTab';
import QuotesNotifications from './QuotesNotifications';
import PayoutsTab from './PayoutsTab';
import AletaBillingTab from './AletaBillingTab';
import LegalBoardTab from './legal-board';
import { fetchLegalBoardBadge } from './legal-board/api';
import PublicUtilityTab from './public-utility/PublicUtilityTab';
import { fetchPublicUtilityBadge } from './public-utility/api';
import { hasAletaBillingFeature } from '../../utils/colla-features';
import { getTreasuryWarnings, isDefaultIban } from '../../utils/treasuryWarnings';
import { validateIban } from '../../utils/iban';
import { getCastellerFullName, getCastellerNameWithMote } from '../../utils/castellerName';
import {
    getOneTimeQuotes,
    getQuoteTypeForBillingMode,
} from './quoteListClassification';

import './TreasuryResponsive.css';
import { formatMoney } from '../../utils/money';

/**
 * Convert a datetime-local value (local time) to UTC ISO string for API submission.
 * datetime-local inputs return values like "2026-02-04T17:35" without timezone info.
 * This function interprets them as local time and converts to UTC.
 * @param {string} localDateTimeValue - Value from datetime-local input
 * @returns {string|null} - UTC ISO string (e.g., "2026-02-04T16:35:00.000Z") or null if empty
 */
const localToUTC = (localDateTimeValue) => {
    if (!localDateTimeValue) return null;
    // Parse as local time and convert to UTC ISO string
    const localDate = new Date(localDateTimeValue);
    return localDate.toISOString();
};

/**
 * Convert a UTC datetime string to local datetime-local format for input display.
 * datetime-local inputs expect values like "2026-02-04T17:35" in local time.
 * @param {string} utcDateString - UTC datetime string from API (e.g., "2026-02-04T16:35:00.000Z")
 * @returns {string} - Local datetime-local format (e.g., "2026-02-04T17:35") or empty string if null
 */
const utcToLocal = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    // Format as local time for datetime-local input: YYYY-MM-DDTHH:mm
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const createEmptyAdminMarkPaidModal = () => ({
    visible: false,
    loading: false,
    submitting: false,
    quoteId: null,
    castellerId: null,
    castellerName: '',
    variants: [],
    selectedVariantId: '',
    numAdults: '1',
    numChildren: '0',
    error: null,
});

const sanitizeNumericText = (value) => (value || '').replace(/[^\d]/g, '');

const parseCountInput = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

// Helper function to get nationality name from code
const getNationalityName = (code) => {
    const nationalities = {
        'ES': 'Espanyola',
        'AD': 'Andorrana',
        'FR': 'Francesa',
        'PT': 'Portuguesa',
        'IT': 'Italiana',
        'DE': 'Alemanya',
        'GB': 'Britànica',
        'OTHER': 'Altra'
    };
    return nationalities[code] || code;
};

function QuotesApp({ socket, userInfo, castellersInfo, ...props }) {
    const navigationRef = useRef(null);
    const showAletaBillingTab = hasAletaBillingFeature();
    const [quotes, setQuotes] = useState([]);
    const [paymentInfo, setPaymentInfo] = useState({ 
        // Bank info
        iban: '', 
        concept: '', 
        // Tax deduction flags
        hasUtilityPublic: false, 
        hasCatalanCensus: false,
        // Organization info
        businessName: '',
        legalName: '',
        nif: '',
        addressLine1: '',
        postalCode: '',
        city: '',
        province: '',
        email: '',
        companyPhone: '',
        // Representative info
        repFirstName: '',
        repLastName: '',
        repDob: '',
        repAddressLine1: '',
        repPostalCode: '',
        repCity: '',
        repEmail: '',
        repPhone: '',
        repNationality: 'ES'
    });
    const getCadenceLabel = (cadence) => {
        const labels = {
            'monthly': 'Mensual',
            'quarterly': 'Trimestral',
            'semiannual': 'Semestral',
            'annual': 'Anual'
        };
        return labels[cadence] || 'Recurrent';
    };

    const getDefaultTitle = (tipus, mes, any, isSubscription = false, cadence = 'monthly') => {
        if (isSubscription) {
            return `Quota recurrent ${getCadenceLabel(cadence)}`;
        }
        const monthNamesFull = ['Gener', 'Febrer', 'Març', 'Abril', 'Maig', 'Juny', 'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre'];
        if (tipus === 'anual') return `Anual ${any}`;
        if (tipus === 'mensual') return `Mensual ${monthNamesFull[mes - 1]} ${any}`;
        if (tipus === 'trimestral') return `Trimestral T${Math.ceil(mes / 3)} ${any}`;
        if (tipus === 'quadrimestral') return `Quadrimestral Q${Math.ceil(mes / 4)} ${any}`;
        if (tipus === 'semestral') return `Semestral S${Math.ceil(mes / 6)} ${any}`;
        return `${tipus} ${any}`;
    };

    const [form, setForm] = useState({
        tipus: 'anual',
        any: new Date().getFullYear(),
        mes: 1,
        titol: getDefaultTitle('anual', 1, new Date().getFullYear()),
        value: 60,
        data_obertura: '',
        data_limit: '',
        voluntaria: true,
        commission_mode: 'user_pays',
        variants: [],
        is_subscription: false,
        cadence: 'monthly'
    });
    const [titolEdited, setTitolEdited] = useState(false);
    const [editingPaymentInfo, setEditingPaymentInfo] = useState(false);
    const [tempPaymentInfo, setTempPaymentInfo] = useState({ 
        // Bank info
        iban: '', 
        concept: '', 
        // Tax deduction flags
        hasUtilityPublic: false, 
        hasCatalanCensus: false,
        // Organization info
        businessName: '',
        legalName: '',
        nif: '',
        addressLine1: '',
        postalCode: '',
        city: '',
        province: '',
        email: '',
        companyPhone: '',
        // Representative info
        repFirstName: '',
        repLastName: '',
        repDob: '',
        repAddressLine1: '',
        repPostalCode: '',
        repCity: '',
        repEmail: '',
        repPhone: '',
        repNationality: 'ES'
    });
    const [selectedQuotePayments, setSelectedQuotePayments] = useState(null);
    const [receiptModal, setReceiptModal] = useState({ visible: false, url: null, base64: null, loading: false });
    const [paymentReceipts, setPaymentReceipts] = useState({});
    const [sendingNotification, setSendingNotification] = useState(false);
    const [allQuotePayments, setAllQuotePayments] = useState({});
    const [quoteRevenues, setQuoteRevenues] = useState({});
    const [quoteVariants, setQuoteVariants] = useState({});
    const [adminMarkPaidModal, setAdminMarkPaidModal] = useState(createEmptyAdminMarkPaidModal);
    const [subscriptionStats, setSubscriptionStats] = useState({}); // { quotaId: { active_subscribers, paused_subscribers, revenue, donations } }
    const [reminderModal, setReminderModal] = useState({ 
        visible: false, 
        quoteId: null, 
        quote: null,
        unpaidCastellers: []
    });
    const [activeTab, setActiveTab] = useState('quotes'); // Default to 'quotes' tab
    const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
    const [legalBoardBadge, setLegalBoardBadge] = useState(null); // { level: 'warning'|'urgent'|null, reasons: [] }
    const [publicUtilityBadge, setPublicUtilityBadge] = useState(null); // { level: 'soon'|'urgent', reason } or null
    const [legalBoardStripeReturn, setLegalBoardStripeReturn] = useState(false); // true when coming back from Stripe onboarding to Junta legal
    const [createdQuote, setCreatedQuote] = useState(null); // Track newly created quote for success message
    const [editingQuote, setEditingQuote] = useState(null); // Track quote being edited
    
    // Add new states for search and filter functionality
    const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('all'); // 'all', 'paid', 'pending', 'not_paid', 'family_paid'

    const { ready: schedulerReady, recognize: recognizeImage } = useScheduler();
    const [ocrData, setOcrData] = useState({ date: null, importValue: null, loading: false, error: null });

    const [ocrReadMode, setOcrReadMode] = useState(false);
    const [ocrGlobalProcessing, setOcrGlobalProcessing] = useState(false);
    const [ocrResults, setOcrResults] = useState({}); // { payment_reference: { date, importValue, loading, error } }
    const [ibanError, setIbanError] = useState(null);
    const [pastQuotesCollapsed, setPastQuotesCollapsed] = useState(true);

    const treasuryWarnings = getTreasuryWarnings({ paymentInfo, stripeConnectStatus, legalBoardBadge, publicUtilityBadge });
    const hasStripeConnectWarning = treasuryWarnings.includes('Pagaments');

    const monthNames = ['Gener', 'Febrer', 'Març', 'Abril', 'Maig', 'Juny', 
                       'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre'];

    // Allowed placeholders for the payment concept input
    const ALLOWED_CONCEPT_PLACEHOLDERS = ['nom', 'cognom', 'cognoms', 'tipus', 'mes', 'any'];

    const findUnknownPlaceholders = (text) => {
        if (!text) return [];
        const matches = Array.from(text.matchAll(/\{([^}]+)\}/g)).map(m => (m[1] || '').trim());
        const unique = Array.from(new Set(matches));
        return unique.filter(token => !ALLOWED_CONCEPT_PLACEHOLDERS.includes(token));
    };


    // Parse the current path to determine the active tab
    const getTabFromPath = () => {
        const path = window.location.pathname;
        const parts = path.split('/');
        const lastPart = parts[parts.length - 1];

        if (lastPart === 'factures-aleta' && !showAletaBillingTab) {
            return 'quotes';
        }

        const availableTabs = showAletaBillingTab
            ? ['info', 'create', 'quotes', 'stripe', 'payouts', 'mensuals', 'patrocinadors', 'notificacions', 'factures-aleta', 'junta-legal', 'utilitat-publica']
            : ['info', 'create', 'quotes', 'stripe', 'payouts', 'mensuals', 'patrocinadors', 'notificacions', 'junta-legal', 'utilitat-publica'];

        if (availableTabs.includes(lastPart)) {
            return lastPart;
        }

        return 'quotes'; // Default tab
    };

    useEffect(() => {
        // Set initial tab based on URL path
        setActiveTab(getTabFromPath());

        // Handle browser back/forward navigation
        const handlePopState = () => {
            setActiveTab(getTabFromPath());
        };

        window.addEventListener('popstate', handlePopState);

        fetchQuotes();
        fetchPaymentInfo();
        fetchStripeConnectStatus();

        // Check URL params for Stripe onboarding return
        const urlParams = new URLSearchParams(window.location.search);
        const stripeParam = urlParams.get('stripe');
        if (stripeParam === 'complete' || stripeParam === 'refresh') {
            // Remove the param from URL. Stripe sends the user back either to the
            // payments tab (default) or to Junta legal when the onboarding was
            // opened from there; in that case we stay on Junta legal and let the
            // tab re-check the real Stripe status.
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            if (getTabFromPath() === 'junta-legal') {
                setLegalBoardStripeReturn(true);
                setActiveTab('junta-legal');
            } else {
                setActiveTab('stripe');
            }
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        const revealActiveTab = () => {
            const navigation = navigationRef.current;
            const active = navigation?.querySelector('.tab-button.active');
            if (!active || !window.matchMedia?.('(max-width: 900px)').matches) return;
            const left = active.offsetLeft - navigation.offsetLeft;
            if (left < navigation.scrollLeft || left + active.offsetWidth > navigation.scrollLeft + navigation.clientWidth) {
                navigation.scrollTo?.({ left: Math.max(0, left - 16), behavior: 'auto' });
            }
        };
        revealActiveTab();
        window.addEventListener('resize', revealActiveTab);
        return () => window.removeEventListener('resize', revealActiveTab);
    }, [activeTab]);

    const refreshLegalBoardBadge = () => {
        if (!props.userId || !can(userInfo, 'ACCESS_QUOTES')) return;
        fetchLegalBoardBadge(props.userId)
            .then(data => {
                if (data?.success) setLegalBoardBadge(data.badge || null);
            })
            .catch(() => {
                // The badge is informative only; failures must never break Tresoreria.
            });
    };

    const refreshPublicUtilityBadge = () => {
        if (!props.userId || !can(userInfo, 'ACCESS_QUOTES')) return;
        fetchPublicUtilityBadge(props.userId)
            .then(data => {
                if (data?.success) setPublicUtilityBadge(data.badge || null);
            })
            .catch(() => {
                // Informative only, as the legal board's badge.
            });
    };

    useEffect(() => {
        refreshLegalBoardBadge();
        refreshPublicUtilityBadge();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.userId, userInfo?.es_junta, userInfo?.es_tecnica]);

    const fetchStripeConnectStatus = () => {
        fetchAPI('/stripe/connect/status', data => {
            if (data?.success) {
                setStripeConnectStatus(data);
            }
        }, false);
    };

    // Helper function to change tab and update URL path
    const changeTab = (tab) => {
        const nextTab = tab === 'factures-aleta' && !showAletaBillingTab ? 'quotes' : tab;
        setActiveTab(nextTab);

        // Always use /quotes as the base URL
        const newPath = `/quotes/${nextTab}`;
        window.history.pushState({ tab: nextTab }, '', newPath);

        // Refetch data based on the tab being switched to
        if (nextTab === 'quotes') {
            fetchQuotes(false);
        } else if (nextTab === 'info') {
            fetchPaymentInfo(false);
        }
        // Note: 'create' tab doesn't need data refetching as it's just a form
    };

    const fetchQuotes = (withCache = true) => {
        fetchAPI('/quotes', data => {
            setQuotes(data);
            // Fetch all payments for each quote
            data.forEach(quote => {
                fetchQuotePayments(quote.id, false);
            });

            // Fetch variants for all quotes
            if (data.length > 0) {
                const quoteIds = data.map(q => q.id).join(',');
                fetchAPI(`/quotes/variants/batch?ids=${quoteIds}`, variantsData => {
                    if (variantsData?.success) {
                        const variantsMap = {};
                        (variantsData.variants || []).forEach(v => {
                            if (!variantsMap[v.quota_id]) {
                                variantsMap[v.quota_id] = [];
                            }
                            variantsMap[v.quota_id].push(v);
                        });
                        setQuoteVariants(variantsMap);
                    }
                }, withCache);
            }
        }, withCache);

        // Also fetch revenue data for all quotes
        fetchAPI('/quotes/revenue', data => {
            const revenueMap = {};
            data.forEach(quote => {
                revenueMap[quote.id] = {
                    revenue: parseFloat(quote.revenue || 0),
                    donations: parseFloat(quote.donations || 0),
                    total_revenue: parseFloat(quote.total_revenue || 0)
                };
            });
            setQuoteRevenues(revenueMap);
        }, withCache);

        // Fetch subscription stats for subscription quotes
        fetchAPI('/subscriptions/quota-stats', data => {
            if (data?.success) {
                setSubscriptionStats(data.stats || {});
            }
        }, withCache);
    };

    const calculateStats = (quoteId) => {
        const payments = allQuotePayments[quoteId] || [];
        const total = payments.length;
        const paidNormally = payments.filter(p => p.pagada).length;
        const familyPaid = payments.filter(p => p.family_paid_by_name && !p.pagada).length;
        const pending = payments.filter(p => !p.pagada && !p.family_paid_by_name && p.payment_reference).length;
        const paid = paidNormally + familyPaid;
        const unpaid = total - paid;

        return { total, paid, unpaid, pending, paidNormally, familyPaid };
    };

    const fetchPaymentInfo = (withCache = true) => {
        fetchAPI('/quotes/payment_info', data => {
            setPaymentInfo(data);
            setTempPaymentInfo(data);
        }, withCache);
    };

    const getQuoteById = (quoteId) => quotes.find(q => q.id === quoteId);

    const getVisibleQuotePayments = (quoteId) => {
        return selectedQuotePayments && selectedQuotePayments.quoteId === quoteId
            ? selectedQuotePayments.payments
            : (allQuotePayments[quoteId] || []);
    };

    const ensureQuoteVariantsLoaded = async (quoteId) => {
        const existingVariants = quoteVariants?.[quoteId];
        if (Array.isArray(existingVariants) && existingVariants.length > 0) {
            return existingVariants;
        }

        const variantsData = await fetchAPIPromise(`/quotes/${quoteId}/variants`, false);
        const resolvedVariants = Array.isArray(variantsData) ? variantsData : [];
        setQuoteVariants(prev => ({ ...prev, [quoteId]: resolvedVariants }));
        return resolvedVariants;
    };

    const refreshQuotePaymentViews = (quoteId) => {
        fetchQuotePayments(quoteId, false);
        if (selectedQuotePayments && selectedQuotePayments.quoteId === quoteId) {
            fetchQuotePayments(quoteId, true);
        }
    };

    const updateLocalPaymentStatus = (quoteId, castellerId, nextPaid) => {
        if (selectedQuotePayments && selectedQuotePayments.quoteId === quoteId) {
            const updatedPayments = selectedQuotePayments.payments.map(p =>
                p.casteller_id === castellerId
                    ? { ...p, pagada: nextPaid }
                    : p
            );
            setSelectedQuotePayments({ ...selectedQuotePayments, payments: updatedPayments });
        }

        setAllQuotePayments(prev => ({
            ...prev,
            [quoteId]: (prev[quoteId] || []).map(p =>
                p.casteller_id === castellerId
                    ? { ...p, pagada: nextPaid }
                    : p
            )
        }));
    };

    const fetchQuotePayments = (quoteId, showModal = true) => {
        fetchAPI(`/quotes/${quoteId}/payments`, data => {
            const { payments: paymentsData, familyPaid } = data.payments ? data : { payments: data, familyPaid: {} };

            // Get all non-hidden castellers
            const nonHiddenCastellers = Object.values(castellersInfo).filter(c => !c.hidden);

            // Create a map of existing payments
            const paymentsMap = {};
            paymentsData.forEach(payment => {
                paymentsMap[payment.casteller_id] = payment;
            });

            // Build complete list including castellers without payment records
            let completePaymentsList = nonHiddenCastellers.map(casteller => {
                if (paymentsMap[casteller.id]) {
                    const payment = paymentsMap[casteller.id];
                    // Add family payment info if this casteller hasn't paid individually but family did
                    if (!payment.pagada && familyPaid[casteller.id]) {
                        return { ...payment, family_paid_by_name: familyPaid[casteller.id] };
                    }
                    return payment;
                } else {
                    // Create a "not paid" entry for castellers without payment records
                    return {
                        id: `notpaid_${casteller.id}`,
                        casteller_id: casteller.id,
                        nom: casteller.nom,
                        cognom: casteller['primer-cognom'],
                        mote: casteller.mote,
                        pagada: false,
                        payment_reference: null,
                        data_pagament: null,
                        family_paid_by_name: familyPaid[casteller.id] || null
                    };
                }
            });

            // Add hidden castellers who have payments or are marked as family_paid
            const addedIds = new Set(nonHiddenCastellers.map(c => c.id));

            // Add hidden castellers from payment records
            paymentsData.forEach(payment => {
                if (!addedIds.has(payment.casteller_id)) {
                    const entry = { ...payment, family_paid_by_name: familyPaid[payment.casteller_id] || null };
                    completePaymentsList.push(entry);
                    addedIds.add(payment.casteller_id);
                }
            });

            // Add hidden castellers from familyPaid who don't have payment records
            Object.entries(familyPaid).forEach(([userId, payerName]) => {
                const uid = parseInt(userId, 10);
                if (!addedIds.has(uid)) {
                    const casteller = castellersInfo[uid];
                    if (casteller && casteller.hidden) {
                        completePaymentsList.push({
                            id: `notpaid_${uid}`,
                            casteller_id: uid,
                            nom: casteller.nom,
                            cognom: casteller['primer-cognom'],
                            mote: casteller.mote,
                            pagada: false,
                            payment_reference: null,
                            data_pagament: null,
                            family_paid_by_name: payerName
                        });
                        addedIds.add(uid);
                    }
                }
            });

            completePaymentsList = completePaymentsList.sort((a, b) => {
                // Sort by payment status: paid first (including family paid), then pending, then not paid
                const aPaid = a.pagada || a.family_paid_by_name;
                const bPaid = b.pagada || b.family_paid_by_name;
                if (aPaid && !bPaid) return -1;
                if (!aPaid && bPaid) return 1;
                if (a.family_paid_by_name && !b.family_paid_by_name) return -1;
                if (!a.family_paid_by_name && b.family_paid_by_name) return 1;
                if (a.payment_reference && !b.payment_reference) return -1;
                if (!a.payment_reference && b.payment_reference) return 1;
                // Then by name
                return getCastellerFullName(a, '').localeCompare(getCastellerFullName(b, ''));
            });
            
            // Store payments for stats calculation
            setAllQuotePayments(prev => ({ ...prev, [quoteId]: completePaymentsList }));
            
            if (showModal) {
                setSelectedQuotePayments({ quoteId, payments: completePaymentsList });
                
                // Only choose an initial filter when opening a different quota.
                // Refreshes after a payment must preserve the user's search and filter.
                if (selectedQuotePayments?.quoteId !== quoteId) {
                    const pendingCount = completePaymentsList.filter(p => !p.pagada && p.payment_reference).length;
                    setPaymentFilter(pendingCount > 0 ? 'pending' : 'all');
                }
                
                // Auto-load receipts for payments with references
                completePaymentsList.forEach(payment => {
                    if (payment.payment_reference && !paymentReceipts[payment.payment_reference]) {
                        loadReceiptForPayment(payment.payment_reference);
                    }
                });
            }
        }, false);
    };

    const loadReceiptForPayment = (paymentReference) => {
        // Mark as loading
        setPaymentReceipts(prev => ({ ...prev, [paymentReference]: { loading: true } }));
        
        // Get the S3 URL for the receipt
        fetchAPI(`/quotes/receipt/${paymentReference}`, data => {
            if (data && data.url) {
                // Fetch the base64 content from the S3 URL
                fetch(data.url)
                    .then(res => res.text())
                    .then(base64Data => {
                        setPaymentReceipts(prev => ({ 
                            ...prev, 
                            [paymentReference]: { loading: false, base64: base64Data }
                        }));
                    })
                    .catch(err => {
                        console.error('Error fetching receipt base64:', err);
                        setPaymentReceipts(prev => ({ 
                            ...prev, 
                            [paymentReference]: { loading: false, error: true }
                        }));
                    });
            } else {
                setPaymentReceipts(prev => ({ 
                    ...prev, 
                    [paymentReference]: { loading: false, error: true }
                }));
            }
        }, err => {
            console.error('Error fetching receipt url:', err);
            setPaymentReceipts(prev => ({ 
                ...prev, 
                [paymentReference]: { loading: false, error: true }
            }));
        });
    };

    const viewReceipt = (paymentReference) => {
        const receipt = paymentReceipts[paymentReference];
        if (receipt && receipt.base64) {
            setReceiptModal({ 
                visible: true, 
                url: null, 
                base64: receipt.base64, 
                loading: false 
            });
        }
    };

    const closeAdminMarkPaidModal = () => {
        setAdminMarkPaidModal(createEmptyAdminMarkPaidModal());
    };

    const openAdminMarkPaidModal = async (quoteId, castellerId) => {
        const visiblePayments = getVisibleQuotePayments(quoteId);
        const selectedPayment = visiblePayments.find(p => p.casteller_id === castellerId);
        const currentQuote = getQuoteById(quoteId);
        const castellerLabel = selectedPayment
            ? getCastellerFullName(selectedPayment, 'Casteller')
            : 'Casteller';

        setAdminMarkPaidModal({
            ...createEmptyAdminMarkPaidModal(),
            visible: true,
            loading: true,
            quoteId,
            castellerId,
            castellerName: castellerLabel,
        });

        try {
            const variants = await ensureQuoteVariantsLoaded(quoteId);
            const preselectedVariantId = selectedPayment?.variant_id ? String(selectedPayment.variant_id) : '';
            const resolvedVariant = variants.find(v => String(v.id) === preselectedVariantId) || null;

            setAdminMarkPaidModal({
                ...createEmptyAdminMarkPaidModal(),
                visible: true,
                loading: false,
                quoteId,
                castellerId,
                castellerName: castellerLabel,
                variants,
                selectedVariantId: resolvedVariant ? String(resolvedVariant.id) : '',
                numAdults: String(selectedPayment?.num_adults ?? 1),
                numChildren: String(selectedPayment?.num_children ?? 0),
                error: variants.length === 0
                    ? 'No s\'han pogut carregar les variants d\'aquesta quota.'
                    : (Number.parseInt(currentQuote?.variants_count, 10) > 1 && !resolvedVariant
                        ? 'Selecciona una variant abans de marcar aquest pagament.'
                        : null),
            });
        } catch (error) {
            console.error('Error loading quote variants for admin mark paid modal:', error);
            setAdminMarkPaidModal({
                ...createEmptyAdminMarkPaidModal(),
                visible: true,
                quoteId,
                castellerId,
                castellerName: castellerLabel,
                error: 'No s\'han pogut carregar les variants. Torna-ho a provar.',
            });
        }
    };

    const confirmAdminMarkPaid = () => {
        const selectedVariant = adminMarkPaidModal.variants.find(
            variant => String(variant.id) === adminMarkPaidModal.selectedVariantId
        );

        if (!selectedVariant) {
            setAdminMarkPaidModal(prev => ({
                ...prev,
                error: 'Has de seleccionar una variant per marcar aquest pagament.',
            }));
            return;
        }

        const hasAdultsAddon = Number(selectedVariant?.has_adults_addon) === 1;
        const hasChildrenAddon = Number(selectedVariant?.has_children_addon) === 1;
        const requestPayload = {
            quotaId: adminMarkPaidModal.quoteId,
            castellerId: adminMarkPaidModal.castellerId,
            variantId: selectedVariant.id,
            numAdults: hasAdultsAddon
                ? Math.max(1, parseCountInput(adminMarkPaidModal.numAdults, 1))
                : 1,
            numChildren: hasChildrenAddon
                ? parseCountInput(adminMarkPaidModal.numChildren, 0)
                : 0,
        };

        setAdminMarkPaidModal(prev => ({
            ...prev,
            submitting: true,
            error: null,
        }));

        postAPI('/quotes/mark_paid', requestPayload, () => {
            closeAdminMarkPaidModal();
            refreshQuotePaymentViews(requestPayload.quotaId);
        }, (error) => {
            console.error('Error marking quote payment as paid with variant:', error);
            setAdminMarkPaidModal(prev => ({
                ...prev,
                submitting: false,
                error: error?.message || error?.error || 'No s\'ha pogut marcar el pagament. Torna-ho a provar.',
            }));
        });
    };

    const togglePaymentStatus = (quoteId, castellerId, currentStatus) => {
        const currentQuote = getQuoteById(quoteId);
        const hasMultipleVariants = Number.parseInt(currentQuote?.variants_count, 10) > 1;

        if (!currentStatus && hasMultipleVariants) {
            openAdminMarkPaidModal(quoteId, castellerId);
            return;
        }

        const action = currentStatus ? 'unmark_paid' : 'mark_paid';
        const visiblePayments = getVisibleQuotePayments(quoteId);
        const selectedPayment = visiblePayments.find(p => p.casteller_id === castellerId);
        const requestPayload = { quotaId: quoteId, castellerId };

        if (!currentStatus && selectedPayment) {
            if (selectedPayment.variant_id !== undefined && selectedPayment.variant_id !== null) {
                requestPayload.variantId = selectedPayment.variant_id;
            }
            if (selectedPayment.num_adults !== undefined && selectedPayment.num_adults !== null) {
                requestPayload.numAdults = selectedPayment.num_adults;
            }
            if (selectedPayment.num_children !== undefined && selectedPayment.num_children !== null) {
                requestPayload.numChildren = selectedPayment.num_children;
            }
        }
        
        updateLocalPaymentStatus(quoteId, castellerId, !currentStatus);
        
        // Send the update to the server
        postAPI(`/quotes/${action}`, requestPayload, () => {
            // On success, refresh data from server to ensure consistency
            refreshQuotePaymentViews(quoteId);
        }, (error) => {
            // On error, revert the changes
            console.error('Error updating payment status:', error);
            alert('Error actualitzant l\'estat del pagament');

            updateLocalPaymentStatus(quoteId, castellerId, currentStatus);

            // Refresh from server to ensure correct state
            refreshQuotePaymentViews(quoteId);
        });
    };

    // Function to format scheduled dates in natural language
    const formatScheduledDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        
        // Days of the week in Catalan
        const daysOfWeek = ['diumenge', 'dilluns', 'dimarts', 'dimecres', 'dijous', 'divendres', 'dissabte'];
        const monthNames = ['gener', 'febrer', 'març', 'abril', 'maig', 'juny', 'juliol', 'agost', 'setembre', 'octubre', 'novembre', 'desembre'];
        
        // Calculate time difference
        const diffMs = date - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffMonths = Math.floor(diffDays / 30.44); // Average days per month
        const diffYears = Math.floor(diffDays / 365.25); // Account for leap years
        
        // Get day of week
        const dayOfWeek = daysOfWeek[date.getDay()];
        
        // Format time
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        // Format date parts
        const day = date.getDate();
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        
        let timeRemaining = '';
        
        if (diffMs < 0) {
            // Date is in the past
            return `${day} de ${month} de ${year} a les ${hours}:${minutes}`;
        }
        
        // Calculate time remaining
        if (diffDays === 0) {
            timeRemaining = 'avui';
        } else if (diffDays === 1) {
            timeRemaining = 'demà';
        } else if (diffDays === 2) {
            timeRemaining = 'demà passat';
        } else if (diffDays < 7) {
            timeRemaining = `aquest ${dayOfWeek}`;
        } else if (diffDays < 14) {
            timeRemaining = `${dayOfWeek} de la setmana que ve`;
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            timeRemaining = `en ${weeks} ${weeks === 1 ? 'setmana' : 'setmanes'}`;
        } else if (diffMonths < 12) {
            const remainingDays = diffDays - Math.floor(diffMonths * 30.44);
            if (diffMonths === 1) {
                timeRemaining = remainingDays > 0 ? `en 1 mes i ${remainingDays} ${remainingDays === 1 ? 'dia' : 'dies'}` : 'en 1 mes';
            } else {
                timeRemaining = remainingDays > 0 ? `en ${diffMonths} mesos i ${remainingDays} ${remainingDays === 1 ? 'dia' : 'dies'}` : `en ${diffMonths} mesos`;
            }
        } else {
            const remainingMonths = diffMonths - (diffYears * 12);
            if (diffYears === 1) {
                timeRemaining = remainingMonths > 0 ? `en 1 any i ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'mesos'}` : 'en 1 any';
            } else {
                timeRemaining = remainingMonths > 0 ? `en ${diffYears} anys i ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'mesos'}` : `en ${diffYears} anys`;
            }
        }
        
        // Build final string
        if (diffDays < 7) {
            return `${timeRemaining} (${day} de ${month}) a les ${hours}:${minutes}`;
        } else {
            return `${timeRemaining} (${dayOfWeek}, ${day} de ${month}${year !== now.getFullYear() ? ' de ' + year : ''}) a les ${hours}:${minutes}`;
        }
    };

    const handleCreate = ev => {
        ev.preventDefault();
        // Save form data before the async call since it will be reset
        const createdFormData = { ...form };
        // Convert dates to UTC before sending to API
        const formWithUTCDates = {
            ...form,
            data_obertura: localToUTC(form.data_obertura),
            data_limit: localToUTC(form.data_limit)
        };
        postAPI('/quotes', formWithUTCDates, (newQuote) => {
            fetchQuotes();
            setForm(prev => {
                const updated = {
                    ...prev,
                    mes: 1,
                    value: prev.tipus === 'anual' ? 60 :
                            prev.tipus === 'semestral' ? 40 :
                            prev.tipus === 'quadrimestral' ? 30 :
                            prev.tipus === 'trimestral' ? 20 : 10,
                    data_obertura: '',
                    data_limit: '',
                    voluntaria: true,
                    commission_mode: 'user_pays',
                    variants: [],
                    is_subscription: false,
                    cadence: 'monthly'
                };
                updated.titol = getDefaultTitle(updated.tipus, updated.mes, updated.any);
                return updated;
            });
            setTitolEdited(false);
            
            // Show success message with notification option
            // Combine API response (id) with form data (value, titol, etc.)
            setCreatedQuote({
                ...newQuote,
                ...createdFormData
            });
        });
    };
    
    const handleDismissCreatedQuote = () => {
        setCreatedQuote(null);
    };

    const sendNewQuoteNotification = (quoteId) => {
        setSendingNotification(true);
        
        // Get quote details - check createdQuote first, then fall back to quotes array
        const quote = createdQuote?.id === quoteId ? createdQuote : quotes.find(q => q.id === quoteId);
        if (!quote) {
            alert('Error: quota no trobada');
            setSendingNotification(false);
            return;
        }
        
        const periodLabel = quote.titol || getPeriodLabel(quote.tipus, quote.mes, quote.any);
        const nonHiddenCastellers = Object.values(castellersInfo).filter(c => !c.hidden);
        
        // Send notification to each non-hidden casteller
        let sentCount = 0;
        nonHiddenCastellers.forEach(casteller => {
            const message = {
                title: 'Nova quota disponible',
                body: `Nova quota disponible: ${periodLabel} (${formatMoney(quote.value)}€)`,
                data: {
                    type: 'quotes',
                    link: '/profile'
                },
                notification_id: `quota_new_${quoteId}_${Date.now()}_${casteller.id}`,
                author: userInfo.id
            };
            
            socket.emit('.send_notification_to_user', casteller.id, message, true);
            sentCount++;
        });
        
        setTimeout(() => {
            alert(`Notificació enviada correctament a ${sentCount} castellers!`);
            setSendingNotification(false);
            setCreatedQuote(null); // Dismiss success view after notification sent
        }, 1000);
    };

    const formatTextForCasteller = (text, casteller) => {
        const nom = casteller.nom || '';
        const cognom1 = casteller['primer-cognom'] || '';
        const cognom2 = casteller['segon-cognom'] || '';
        const cognoms = [cognom1, cognom2].filter(Boolean).join(' ');
        const sobrenom = casteller.mote || casteller.nom || '';
        
        return text
            .replaceAll('{nom}', nom)
            .replaceAll('{cognom}', cognom1)
            .replaceAll('{cognoms}', cognoms)
            .replaceAll('{sobrenom}', sobrenom);
    };

    const openReminderModal = (quoteId) => {
        const quote = quotes.find(q => q.id === quoteId);
        if (!quote) return;
        
        const payments = allQuotePayments[quoteId] || [];
        const paidCastellerIds = payments.filter(p => p.pagada || p.family_paid_by_name).map(p => p.casteller_id);

        // Get unpaid non-hidden castellers (excluding paid and family-paid)
        const unpaidCastellers = Object.values(castellersInfo)
            .filter(c => !c.hidden && !paidCastellerIds.includes(c.id));
        
        setReminderModal({
            visible: true,
            quoteId,
            quote,
            unpaidCastellers,
            isSubscription: false
        });
    };

    const openSubscriptionReminderModal = (quoteId) => {
        const quote = quotes.find(q => q.id === quoteId);
        if (!quote) return;

        // Fetch current subscribers for this quote
        fetchAPI(`/subscriptions/quota/${quoteId}`, data => {
            if (!data?.success) return;

            const subscribedIds = new Set(
                (data.subscriptions || [])
                    .filter(s => s.status === 'active' || s.status === 'paused')
                    .map(s => s.casteller_id)
            );

            // Non-subscribed, non-hidden castellers
            const notSubscribed = Object.values(castellersInfo)
                .filter(c => !c.hidden && !subscribedIds.has(c.id));

            setReminderModal({
                visible: true,
                quoteId,
                quote,
                unpaidCastellers: notSubscribed,
                isSubscription: true
            });
        });
    };

    const sendReminder = ({ title, body, recipients }) => {
        setSendingNotification(true);
        
        const { quoteId } = reminderModal;
        
        // Send notification to each recipient
        let sentCount = 0;
        recipients.forEach(casteller => {
            const message = {
                title: formatTextForCasteller(title, casteller),
                body: formatTextForCasteller(body, casteller),
                data: {
                    type: 'quotes',
                    link: '/profile'
                },
                notification_id: `quota_reminder_${quoteId}_${Date.now()}_${casteller.id}`,
                author: userInfo.id
            };
            
            socket.emit('.send_notification_to_user', casteller.id, message, true);
            sentCount++;
        });
        
        setTimeout(() => {
            alert(`Recordatori enviat correctament a ${sentCount} castellers!`);
            setSendingNotification(false);
            setReminderModal({ ...reminderModal, visible: false });
        }, 1000);
    };

    const sendUnpaidNotification = (quoteId) => {
        openReminderModal(quoteId);
    };

    const deleteQuote = id => {
        if (window.confirm('Segur que vols eliminar aquesta quota? És irreversible!')) {
            deleteAPI(`/quotes/${id}`, fetchQuotes);
        }
    };

    const updateQuote = () => {
        if (!editingQuote) return;
        // Convert dates to UTC before sending to API
        putAPI(`/quotes/${editingQuote.id}`, {
            titol: editingQuote.titol,
            value: editingQuote.value,
            data_obertura: localToUTC(editingQuote.data_obertura),
            data_limit: localToUTC(editingQuote.data_limit),
            voluntaria: editingQuote.voluntaria,
            commission_mode: editingQuote.commission_mode
        }, () => {
            setEditingQuote(null);
            fetchQuotes();
        });
    };

    const updatePaymentInfo = () => {
        // Validate IBAN before saving
        const ibanValidation = validateIban(tempPaymentInfo.iban);
        if (!ibanValidation.valid) {
            setIbanError(ibanValidation.error);
            return;
        }
        setIbanError(null);
        
        postAPI('/quotes/payment_info', tempPaymentInfo, () => {
            setPaymentInfo(tempPaymentInfo);
            setEditingPaymentInfo(false);
        });
    };

    const change = (field, value) => {
        setForm(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-adjust import based on tipus
            if (field === 'tipus') {
                updated.value = value === 'anual' ? 60 :
                                value === 'quadrimestral' ? 30 :
                                value === 'trimestral' ? 20 : 10;
                updated.mes = 1;
            }

            // Sync tipus with cadence for subscription quotes
            if (field === 'is_subscription' || field === 'cadence') {
                const previousType = updated.tipus;
                updated.tipus = getQuoteTypeForBillingMode({
                    isSubscription: updated.is_subscription,
                    cadence: updated.cadence,
                    currentType: previousType,
                });
                if (!updated.is_subscription && previousType === 'mensual') {
                    updated.mes = 1;
                }
            }

            // Auto-update title based on subscription status or regular fields
            if (!titolEdited) {
                if (field === 'is_subscription' || field === 'cadence' || field === 'tipus' || field === 'any' || field === 'mes') {
                    const { tipus, any, mes, is_subscription, cadence } = updated;
                    updated.titol = getDefaultTitle(tipus, mes, any, is_subscription, cadence);
                }
            }

            return updated;
        });
        if (field === 'titol') {
            setTitolEdited(true);
        }
    };

    const getPeriodLabel = (tipus, mes, any) => {
        if (tipus === 'anual') return `${any}`;
        if (tipus === 'mensual') return `${monthNames[mes - 1]} ${any}`;
        if (tipus === 'trimestral') {
            const trimestre = Math.ceil(mes / 3);
            const startMonth = (trimestre - 1) * 3;
            const endMonth = startMonth + 2;
            return `${monthNames[startMonth]} - ${monthNames[endMonth]} ${any}`;
        }
        if (tipus === 'quadrimestral') {
            const quadrimestre = Math.ceil(mes / 4);
            const startMonth = (quadrimestre - 1) * 4;
            const endMonth = Math.min(startMonth + 3, 11);
            return `${monthNames[startMonth]} - ${monthNames[endMonth]} ${any}`;
        }
        if (tipus === 'semestral') {
            const semestre = Math.ceil(mes / 6);
            const startMonth = (semestre - 1) * 6;
            const endMonth = startMonth + 5;
            return `${monthNames[startMonth]} - ${monthNames[endMonth]} ${any}`;
        }
        return '';
    };

    const getAvailableMonths = () => {
        if (form.tipus === 'anual') return [];
        if (form.tipus === 'mensual') return [...Array(12)].map((_, i) => i + 1);
        if (form.tipus === 'trimestral') return [1, 4, 7, 10]; // First month of each trimester
        if (form.tipus === 'quadrimestral') return [1, 5, 9]; // First month of each quadrimester
        if (form.tipus === 'semestral') return [1, 9]; // First month of each semester
        return [];
    };

    const resetReceiptModal = () => {
        setReceiptModal({ visible: false, url: null, base64: null, loading: false });
        setOcrData({ date: null, importValue: null, loading: false, error: null }); // Also reset single OCR data
    };

    // Quote cards use scoped classes so the layout can respond to the viewport.

    // Render a quote card.
    const renderQuoteCard = (q, isScheduled = false) => {
        const stats = calculateStats(q.id);
        const periodLabel = q.titol || getPeriodLabel(q.tipus, q.mes, q.any);

        // Get type badge style
        const getTypeBadgeClass = (tipus) => [
            'treasury-quote-typeBadge',
            ({ anual: 'treasury-quote-typeBadgeAnual', quadrimestral: 'treasury-quote-typeBadgeQuadrimestral',
                trimestral: 'treasury-quote-typeBadgeTrimestral', mensual: 'treasury-quote-typeBadgeMensual' })[tipus]
        ].filter(Boolean).join(' ');

        const showScheduleInfo = isScheduled && q.data_obertura && new Date(q.data_obertura) > new Date();
        const hasDeadline = q.data_limit && q.data_limit !== null;
        const deadlineExpired = hasDeadline && new Date(q.data_limit) < new Date();

        return (
            <div key={q.id} className="treasury-quote-card">
                {/* Main content row */}
                <div className="treasury-quote-mainRow">
                    {/* Info Section: Type badge + Period */}
                    <div className="treasury-quote-infoSection">
                        {q.is_subscription ? (
                            <span className={['treasury-quote-typeBadge', 'treasury-quote-typeBadgeSubscription'].filter(Boolean).join(' ')}>
                                🔄 {getCadenceLabel(q.cadence)}
                            </span>
                        ) : (
                            <span className={getTypeBadgeClass(q.tipus)}>{q.tipus}</span>
                        )}
                        <span className="treasury-quote-period">{periodLabel}</span>
                    </div>

                    {/* Stats Section */}
                    <div className="treasury-quote-statsSection">
                        {q.is_subscription ? (
                            <>
                                <div className="treasury-quote-statItem">
                                    <div className="treasury-quote-statNumber">{subscriptionStats[q.id]?.active_subscribers || 0}</div>
                                    <div className="treasury-quote-statLabel">ACTIUS</div>
                                </div>
                                <div className="treasury-quote-statDivider">•</div>
                                <div className="treasury-quote-statItem">
                                    <div className="treasury-quote-statNumber">{subscriptionStats[q.id]?.paused_subscribers || 0}</div>
                                    <div className="treasury-quote-statLabel">PAUSATS</div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="treasury-quote-statItem">
                                    <div className="treasury-quote-statNumber">{stats.pending}</div>
                                    <div className="treasury-quote-statLabel">PENDENTS</div>
                                </div>
                                <div className="treasury-quote-statDivider">•</div>
                                <div className="treasury-quote-statItem">
                                    <div className="treasury-quote-statNumber">{stats.paid}</div>
                                    <div className="treasury-quote-statLabel">PAGATS</div>
                                </div>
                                <div className="treasury-quote-statDivider">/</div>
                                <div className="treasury-quote-statItem">
                                    <div className="treasury-quote-statNumber">{stats.total}</div>
                                    <div className="treasury-quote-statLabel">TOTAL</div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Actions Section */}
                    <div className="treasury-quote-actionsSection">
                        {!q.is_subscription ? (
                            <>
                                <button
                                    className="treasury-quote-primaryAction"
                                    onClick={() => fetchQuotePayments(q.id, true)}
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#2563eb'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#3b82f6'; }}
                                >
                                    <span>👥</span> Veure pagaments
                                </button>
                                {stats.unpaid > 0 ? (
                                    <button
                                        className="treasury-quote-reminderAction"
                                        onClick={() => openReminderModal(q.id)}
                                        disabled={sendingNotification}
                                        title="Gestionar i enviar recordatoris"
                                        onMouseOver={(e) => {
                                            if (!sendingNotification) {
                                                e.currentTarget.style.background = '#d97706';
                                            }
                                        }}
                                        onMouseOut={(e) => { e.currentTarget.style.background = '#f59e0b'; }}
                                    >
                                        <span>🔔</span> Gestionar recordatoris
                                    </button>
                                ) : null}
                            </>
                        ) : (
                            (() => {
                                const totalNonHidden = Object.values(castellersInfo).filter(c => !c.hidden).length;
                                const subscribed = (subscriptionStats[q.id]?.active_subscribers || 0) + (subscriptionStats[q.id]?.paused_subscribers || 0);
                                return totalNonHidden - subscribed > 0 ? (
                                    <button
                                        className="treasury-quote-reminderAction"
                                        onClick={() => openSubscriptionReminderModal(q.id)}
                                        disabled={sendingNotification}
                                        title="Enviar recordatori als no subscrits"
                                        onMouseOver={(e) => {
                                            if (!sendingNotification) {
                                                e.currentTarget.style.background = '#d97706';
                                            }
                                        }}
                                        onMouseOut={(e) => { e.currentTarget.style.background = '#f59e0b'; }}
                                    >
                                        <span>🔔</span> Gestionar recordatoris
                                    </button>
                                ) : null;
                            })()
                        )}

                        <div className="treasury-quote-secondaryActions">
                            {can(userInfo, 'MANAGE_QUOTES') ? (
                                <button
                                    className="treasury-quote-iconButton"
                                    onClick={() => setEditingQuote({
                                        ...q,
                                        // Convert UTC dates to local format for editing
                                        // This ensures localToUTC works correctly on save
                                        data_obertura: utcToLocal(q.data_obertura),
                                        data_limit: utcToLocal(q.data_limit)
                                    })}
                                    aria-label="Editar quota" title="Editar quota"
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#cbd5e1'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                                >
                                    <span aria-hidden="true">✏️</span><span className="treasury-mobile-action-label">Editar</span>
                                </button>
                            ) : null}
                            {can(userInfo, 'MANAGE_QUOTES') ? (
                                <button
                                    className={['treasury-quote-iconButton', 'treasury-quote-iconButtonDanger'].filter(Boolean).join(' ')}
                                    onClick={() => deleteQuote(q.id)}
                                    aria-label="Eliminar quota" title="Eliminar quota"
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                                >
                                    <span aria-hidden="true">🗑️</span><span className="treasury-mobile-action-label">Eliminar</span>
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                {/* Schedule Info Row (only for scheduled quotes) */}
                {showScheduleInfo ? (
                    <div className="treasury-quote-scheduleRow">
                        <span className="treasury-quote-scheduleBadge">
                            🕐 S'obrirà {formatScheduledDate(q.data_obertura)}
                        </span>
                    </div>
                ) : null}

                {/* Deadline Info Row */}
                {hasDeadline ? (
                    <div className="treasury-quote-deadlineRow">
                        <span>{deadlineExpired ? `🔒 Es va tancar el dia ${formatScheduledDate(q.data_limit)}` : `⏰ Data límit: ${formatScheduledDate(q.data_limit)}`}</span>
                    </div>
                ) : null}
            </div>
        );
    };

    const closePaymentsModal = () => {
        setSelectedQuotePayments(null);
        setOcrReadMode(false);
        setOcrGlobalProcessing(false);
        setOcrResults({});
        setPaymentSearchTerm(''); // Reset search
        setPaymentFilter('all'); // Reset filter
        closeAdminMarkPaidModal();
    };

    const selectedAdminMarkPaidVariant = adminMarkPaidModal.variants.find(
        variant => String(variant.id) === adminMarkPaidModal.selectedVariantId
    ) || null;
    const adminMarkPaidHasAdultsAddon = Number(selectedAdminMarkPaidVariant?.has_adults_addon) === 1;
    const adminMarkPaidHasChildrenAddon = Number(selectedAdminMarkPaidVariant?.has_children_addon) === 1;
    const adminMarkPaidIsFamilyVariant = adminMarkPaidHasAdultsAddon || adminMarkPaidHasChildrenAddon;

    const toggleOcrMode = async () => {
        if (ocrGlobalProcessing) return; 

        if (ocrReadMode) {
            setOcrReadMode(false);
        } else {
            if (!schedulerReady) {
                alert("El motor de lectura OCR no està llest encara. Si us plau, espera uns moments i torna-ho a provar.");
                return;
            }
            setOcrReadMode(true);
            setOcrGlobalProcessing(true);

            const paymentsToProcess = (selectedQuotePayments?.payments || []).filter(
                p => p.payment_reference && paymentReceipts[p.payment_reference]?.base64 && !ocrResults[p.payment_reference]
            );

            const initialOcrResults = { ...ocrResults };
            paymentsToProcess.forEach(p => {
                initialOcrResults[p.payment_reference] = { loading: true, date: null, importValue: null, error: null };
            });
            setOcrResults(initialOcrResults);

            const promises = paymentsToProcess.map(async (payment) => {
                try {
                    const result = await recognizeImage(paymentReceipts[payment.payment_reference].base64);
                    setOcrResults(prev => ({
                        ...prev,
                        [payment.payment_reference]: { date: result.date, importValue: result.import, loading: false, error: null }
                    }));
                } catch (err) {
                    console.error('OCR error for payment', payment.payment_reference, err);
                    setOcrResults(prev => ({
                        ...prev,
                        [payment.payment_reference]: { loading: false, error: 'Error OCR', date: null, importValue: null }
                    }));
                }
            });

            await Promise.all(promises);
            setOcrGlobalProcessing(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'info':
                return renderPaymentInfo();
            case 'create':
                return renderCreateQuote();
            case 'stripe':
                return <StripeConnectTab userInfo={userInfo} paymentInfo={paymentInfo} onChangeTab={changeTab} />;
            case 'payouts':
                return <PayoutsTab stripeConnectStatus={stripeConnectStatus} onChangeTab={changeTab} />;
            case 'mensuals':
                return <SubscriptionsTab userInfo={userInfo} castellersInfo={castellersInfo} stripeConnectStatus={stripeConnectStatus} />;
            case 'patrocinadors':
                return <SponsorsTab userInfo={userInfo} stripeConnectStatus={stripeConnectStatus} />;
            case 'notificacions':
                return <QuotesNotifications userInfo={userInfo} />;
            case 'factures-aleta':
                return showAletaBillingTab
                    ? <AletaBillingTab userInfo={userInfo} userId={props.userId} />
                    : renderQuotesTable();
            case 'junta-legal':
                return (
                    <LegalBoardTab
                        userInfo={userInfo}
                        userId={props.userId}
                        castellersInfo={castellersInfo}
                        stripeReturn={legalBoardStripeReturn}
                        onStripeReturnHandled={() => setLegalBoardStripeReturn(false)}
                        onBadgeChange={(badge) => setLegalBoardBadge(badge || null)}
                        onChangeTab={changeTab}
                    />
                );
            case 'utilitat-publica':
                return (
                    <PublicUtilityTab
                        userInfo={userInfo}
                        userId={props.userId}
                        onBadgeChange={(badge) => setPublicUtilityBadge(badge || null)}
                        onChangeTab={changeTab}
                    />
                );
            case 'quotes':
            default:
                return renderQuotesTable();
        }
    };

    const renderPaymentInfo = () => {
        const unknownPlaceholders = findUnknownPlaceholders(tempPaymentInfo.concept);
        const handleFieldChange = (field, value) => {
            setTempPaymentInfo(prev => ({ ...prev, [field]: value }));
        };
        
        return (
            <div className="payment-info-section fiscal-data-section">
                <h2>Dades fiscals de la colla</h2>
                <p className="section-description">
                    Aquestes dades s'utilitzen per generar rebuts, deduccions fiscals i per activar pagaments amb targeta.
                </p>
                
                {!editingPaymentInfo ? (
                    <div className="fiscal-data-display">
                        {/* Organization Info */}
                        <div className="info-section">
                            <h3>📋 Dades de la colla</h3>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="label">Nom comercial:</span>
                                    <span className="value">{paymentInfo.businessName || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Raó social:</span>
                                    <span className="value">{paymentInfo.legalName || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">NIF:</span>
                                    <span className="value">{paymentInfo.nif || <em className="not-set">No configurat</em>}</span>
                                </div>
                            </div>
                        </div>

                        {/* Address */}
                        <div className="info-section">
                            <h3>📍 Adreça fiscal</h3>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="label">Adreça:</span>
                                    <span className="value">{paymentInfo.addressLine1 || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Codi postal:</span>
                                    <span className="value">{paymentInfo.postalCode || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Ciutat:</span>
                                    <span className="value">{paymentInfo.city || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Província:</span>
                                    <span className="value">{paymentInfo.province || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{paymentInfo.email || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Telèfon:</span>
                                    <span className="value">{paymentInfo.companyPhone || <em className="not-set">No configurat</em>}</span>
                                </div>
                            </div>
                        </div>

                        {/* Bank Info */}
                        <div className="info-section">
                            <h3>🏦 Compte bancari</h3>
                            <div className="info-grid">
                        <div className="info-row">
                            <span className="label">IBAN:</span>
                                    <span className="value">{!isDefaultIban(paymentInfo.iban) ? paymentInfo.iban : <em className="not-set">No configurat</em>}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Concepte:</span>
                                    <span className="value">{paymentInfo.concept || <em className="not-set">No configurat</em>}</span>
                        </div>
                            </div>
                        </div>

                        {/* Representative */}
                        <div className="info-section">
                            <h3>👤 Representant legal</h3>
                            <div className="info-grid">
                                <div className="info-row">
                                    <span className="label">Nom:</span>
                                    <span className="value">{paymentInfo.repFirstName && paymentInfo.repLastName ? `${paymentInfo.repFirstName} ${paymentInfo.repLastName}` : <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Data de naixement:</span>
                                    <span className="value">{paymentInfo.repDob ? new Date(paymentInfo.repDob).toLocaleDateString('ca-ES') : <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Adreça:</span>
                                    <span className="value">{paymentInfo.repAddressLine1 || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Població:</span>
                                    <span className="value">{paymentInfo.repCity && paymentInfo.repPostalCode ? `${paymentInfo.repPostalCode} ${paymentInfo.repCity}` : <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{paymentInfo.repEmail || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Telèfon:</span>
                                    <span className="value">{paymentInfo.repPhone || <em className="not-set">No configurat</em>}</span>
                                </div>
                                <div className="info-row">
                                    <span className="label">Nacionalitat:</span>
                                    <span className="value">{paymentInfo.repNationality ? getNationalityName(paymentInfo.repNationality) : <em className="not-set">No configurat</em>}</span>
                                </div>
                            </div>
                        </div>

                        {/* Tax Deductions */}
                        <div className="info-section">
                            <h3>📊 Deduccions fiscals</h3>
                            <div className="info-grid">
                        <div className="info-row">
                            <span className="label">Utilitat pública:</span>
                            <span className="value">{paymentInfo.hasUtilityPublic ? '✅ Sí (80%/40% deducció)' : '❌ No'}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Cens català:</span>
                            <span className="value">{paymentInfo.hasCatalanCensus ? '✅ Sí (15% deducció)' : '❌ No'}</span>
                        </div>
                            </div>
                        </div>

                        <button className="bank-details-edit-button" onClick={() => setEditingPaymentInfo(true)}>
                            <span>✏️</span> Editar dades
                        </button>
                    </div>
                ) : (
                    <div className="fiscal-data-edit">
                        {/* Organization Info Form */}
                        <div className="form-section">
                            <h4>📋 Dades de la colla</h4>
                        <div className="form-group">
                                <label htmlFor="businessName">Nom comercial</label>
                                <input 
                                    id="businessName"
                                    type="text" 
                                    placeholder="Nom públic de la colla"
                                    value={tempPaymentInfo.businessName}
                                    onChange={e => handleFieldChange('businessName', e.target.value)}
                                />
                                <small>El nom que veuran els socis als pagaments</small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="legalName">Raó social *</label>
                                <input 
                                    id="legalName"
                                    type="text" 
                                    placeholder="Associació Cultural..."
                                    value={tempPaymentInfo.legalName}
                                    onChange={e => handleFieldChange('legalName', e.target.value)}
                                />
                                <small>El nom legal registrat de l'associació</small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="nif">NIF *</label>
                                <input 
                                    id="nif"
                                    type="text" 
                                    placeholder="G12345678"
                                    value={tempPaymentInfo.nif}
                                    onChange={e => handleFieldChange('nif', e.target.value.toUpperCase())}
                                />
                                <small>NIF de l'associació (lletra + 8 dígits)</small>
                            </div>
                        </div>

                        {/* Address Form */}
                        <div className="form-section">
                            <h4>📍 Adreça fiscal</h4>
                            <div className="form-group">
                                <label htmlFor="addressLine1">Adreça *</label>
                                <input 
                                    id="addressLine1"
                                    type="text" 
                                    placeholder="Carrer, número, pis..."
                                    value={tempPaymentInfo.addressLine1}
                                    onChange={e => handleFieldChange('addressLine1', e.target.value)}
                                />
                            </div>
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label htmlFor="postalCode">Codi postal *</label>
                                    <input 
                                        id="postalCode"
                                        type="text" 
                                        placeholder="08001"
                                        value={tempPaymentInfo.postalCode}
                                        onChange={e => handleFieldChange('postalCode', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="city">Ciutat *</label>
                                    <input 
                                        id="city"
                                        type="text" 
                                        placeholder="Barcelona"
                                        value={tempPaymentInfo.city}
                                        onChange={e => handleFieldChange('city', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label htmlFor="province">Província</label>
                                    <input 
                                        id="province"
                                        type="text" 
                                        placeholder="Barcelona"
                                        value={tempPaymentInfo.province}
                                        onChange={e => handleFieldChange('province', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="email">Email de contacte</label>
                                    <input 
                                        id="email"
                                        type="email" 
                                        placeholder="email@exemple.com"
                                        value={tempPaymentInfo.email}
                                        onChange={e => handleFieldChange('email', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="companyPhone">Telèfon de l'entitat *</label>
                                    <input 
                                        id="companyPhone"
                                        type="tel" 
                                        placeholder="+34 600 000 000"
                                        value={tempPaymentInfo.companyPhone}
                                        onChange={e => handleFieldChange('companyPhone', e.target.value)}
                                    />
                                    <small>Format internacional amb prefix (+34 per Espanya)</small>
                                </div>
                            </div>
                        </div>

                        {/* Bank Info Form */}
                        <div className="form-section">
                            <h4>🏦 Compte bancari</h4>
                            <div className="form-group">
                                <label htmlFor="iban">IBAN *</label>
                            <input 
                                id="iban"
                                type="text" 
                                placeholder="ES00 0000 0000 0000 0000 0000"
                                value={tempPaymentInfo.iban}
                                    onChange={e => {
                                        handleFieldChange('iban', e.target.value);
                                        setIbanError(null); // Clear error when user edits
                                    }}
                            />
                                <small>On es transferiran els pagaments amb targeta i per a transferències bancàries</small>
                                {ibanError ? (
                                    <div className="field-error">{ibanError}</div>
                                ) : null}
                        </div>
                        <div className="form-group">
                            <label htmlFor="concept">Concepte de pagament</label>
                            <input 
                                id="concept"
                                type="text" 
                                placeholder="Quota {nom} {cognom} - {tipus} {mes} {any}"
                                value={tempPaymentInfo.concept}
                                    onChange={e => handleFieldChange('concept', e.target.value)}
                            />
                            <small>Pots usar: {'{nom}'}, {'{cognom}'}, {'{cognoms}'}, {'{tipus}'}, {'{mes}'}, {'{any}'}</small>
                            {unknownPlaceholders.length > 0 ? (
                                    <div className="field-error">
                                    Atenció: {unknownPlaceholders.map(t => `{${t}}`).join(', ')} {unknownPlaceholders.length > 1 ? 'no són etiquetes vàlides' : 'no és una etiqueta vàlida'}
                                </div>
                            ) : null}
                            </div>
                        </div>

                        {/* Representative Form */}
                        <div className="form-section">
                            <h4>👤 Representant legal</h4>
                            <p className="section-hint">Persona autoritzada a representar l'associació (president/a, tresorer/a...)</p>
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label htmlFor="repFirstName">Nom *</label>
                                    <input 
                                        id="repFirstName"
                                        type="text" 
                                        placeholder="Nom"
                                        value={tempPaymentInfo.repFirstName}
                                        onChange={e => handleFieldChange('repFirstName', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="repLastName">Cognoms *</label>
                                    <input 
                                        id="repLastName"
                                        type="text" 
                                        placeholder="Cognoms"
                                        value={tempPaymentInfo.repLastName}
                                        onChange={e => handleFieldChange('repLastName', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="repDob">Data de naixement *</label>
                                <input 
                                    id="repDob"
                                    type="date" 
                                    value={tempPaymentInfo.repDob}
                                    onChange={e => handleFieldChange('repDob', e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="repAddressLine1">Adreça personal *</label>
                                <input 
                                    id="repAddressLine1"
                                    type="text" 
                                    placeholder="Carrer, número, pis..."
                                    value={tempPaymentInfo.repAddressLine1}
                                    onChange={e => handleFieldChange('repAddressLine1', e.target.value)}
                                />
                            </div>
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label htmlFor="repPostalCode">Codi postal *</label>
                                    <input 
                                        id="repPostalCode"
                                        type="text" 
                                        placeholder="08001"
                                        value={tempPaymentInfo.repPostalCode}
                                        onChange={e => handleFieldChange('repPostalCode', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="repCity">Ciutat *</label>
                                    <input 
                                        id="repCity"
                                        type="text" 
                                        placeholder="Barcelona"
                                        value={tempPaymentInfo.repCity}
                                        onChange={e => handleFieldChange('repCity', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label htmlFor="repEmail">Email del representant *</label>
                                    <input 
                                        id="repEmail"
                                        type="email" 
                                        placeholder="representant@email.com"
                                        value={tempPaymentInfo.repEmail}
                                        onChange={e => handleFieldChange('repEmail', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="repPhone">Telèfon del representant *</label>
                                    <input 
                                        id="repPhone"
                                        type="tel" 
                                        placeholder="+34 600 000 000"
                                        value={tempPaymentInfo.repPhone}
                                        onChange={e => handleFieldChange('repPhone', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="repNationality">Nacionalitat del representant *</label>
                                <select
                                    id="repNationality"
                                    value={tempPaymentInfo.repNationality || 'ES'}
                                    onChange={e => handleFieldChange('repNationality', e.target.value)}
                                >
                                    <option value="ES">Espanyola</option>
                                    <option value="AD">Andorrana</option>
                                    <option value="FR">Francesa</option>
                                    <option value="PT">Portuguesa</option>
                                    <option value="IT">Italiana</option>
                                    <option value="DE">Alemanya</option>
                                    <option value="GB">Britànica</option>
                                    <option value="OTHER">Altra</option>
                                </select>
                            </div>
                            <p className="section-note">
                                El DNI del representant es verificarà durant el procés de verificació de Stripe.
                            </p>
                            <p className="section-note">
                                El representant es pot canviar quan es vulgui, però cal que algú amb rol a la colla (president/a, tresorer/a, secretari/ària) quedi com a contacte.
                            </p>
                        </div>
                        
                        {/* Tax Deduction Config */}
                        <div className="form-section deduction-config">
                            <h4>📊 Configuració de deduccions fiscals</h4>
                            <div className="checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={tempPaymentInfo.hasUtilityPublic}
                                        onChange={e => setTempPaymentInfo(prev => ({ 
                                            ...prev, 
                                            hasUtilityPublic: e.target.checked
                                        }))}
                                    />
                                    <span className="checkmark"></span>
                                    La colla té declaració d'utilitat pública
                                    <small>Permet deducció fiscal del 80% pels primers 250€, 40% per la resta. Es marca sol quan a Utilitat pública indiques el Model 036.</small>
                                </label>
                            </div>
                            
                            <div className="checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={tempPaymentInfo.hasCatalanCensus}
                                        onChange={e => setTempPaymentInfo(prev => ({ 
                                            ...prev, 
                                            hasCatalanCensus: e.target.checked
                                        }))}
                                    />
                                    <span className="checkmark"></span>
                                    La colla està en el cens d'entitats catalanes
                                    <small>Afegeix un 15% addicional de deducció</small>
                                </label>
                            </div>
                            
                            {(tempPaymentInfo.hasUtilityPublic || tempPaymentInfo.hasCatalanCensus) ? (
                                <div className="deduction-preview">
                                    <strong>
                                        {tempPaymentInfo.hasUtilityPublic && tempPaymentInfo.hasCatalanCensus 
                                            ? 'Deducció total: 95% pels primers 250€, 55% per la resta'
                                            : tempPaymentInfo.hasUtilityPublic 
                                                ? 'Deducció total: 80% pels primers 250€, 40% per la resta'
                                                : 'Deducció total: 15%'}
                                    </strong>
                                </div>
                            ) : null}
                        </div>
                        
                        <div className="form-actions">
                            <button
                                className="save-button"
                                onClick={updatePaymentInfo}
                            >
                                💾 Guardar
                            </button>
                            <button
                                className="cancel-button"
                                onClick={() => {
                                    setEditingPaymentInfo(false);
                                    setTempPaymentInfo(paymentInfo);
                                    setIbanError(null);
                                }}
                            >
                                Cancel·lar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderCreateQuote = () => (
        can(userInfo, 'MANAGE_QUOTES') ? (
            <CreateQuoteForm
                form={form}
                onChange={change}
                onSubmit={handleCreate}
                getAvailableMonths={getAvailableMonths}
                monthNames={monthNames}
                getPeriodLabel={getPeriodLabel}
                createdQuote={createdQuote}
                onSendNotification={sendNewQuoteNotification}
                onDismissSuccess={handleDismissCreatedQuote}
                sendingNotification={sendingNotification}
                stripeConnectStatus={stripeConnectStatus}
            />
        ) : (
            <div className="empty-state">
                <p>No tens permisos per crear quotes</p>
            </div>
        )
    );

    const renderQuotesTable = () => {
        // Separate scheduled quotes from active one-time quotes. Recurring quotes
        // are managed in SubscriptionsTab regardless of their cadence.
        const now = new Date();
        const currentYear = now.getFullYear();
        const oneTimeQuotes = getOneTimeQuotes(quotes);
        const scheduledQuotes = oneTimeQuotes.filter(q => q.data_obertura && new Date(q.data_obertura) > now);
        
        // Helper to check if quote deadline has passed
        const isQuoteExpired = (q) => q.data_limit && new Date(q.data_limit) < now;
        
        // Active quotes: current year, not scheduled, not expired
        const activeQuotes = oneTimeQuotes.filter(q =>
            (!q.data_obertura || new Date(q.data_obertura) <= now) &&
            q.any >= currentYear &&
            !isQuoteExpired(q)
        );
        
        // Past quotes: either from previous years OR expired (deadline passed)
        const pastQuotes = oneTimeQuotes.filter(q =>
            (!q.data_obertura || new Date(q.data_obertura) <= now) &&
            (q.any < currentYear || isQuoteExpired(q))
        );
        
        return (
            <div className="quotes-section">
                <QuoteRevenueChart 
                    quotes={quotes}
                    allQuotePayments={allQuotePayments}
                    subscriptionStats={subscriptionStats}
                    castellersInfo={castellersInfo}
                />
                
                {/* Scheduled Quotes Section */}
                {scheduledQuotes.length > 0 ? (
                    <div style={{ marginBottom: '32px' }}>
                        <h2 style={{
                            margin: '0 0 16px 0',
                            padding: '0',
                            color: '#334155',
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            lineHeight: '1.3',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        }}>📅 Quotes programades</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
                            {scheduledQuotes.map(q => renderQuoteCard(q, true))}
                        </div>
                    </div>
                ) : null}
                
                {/* Active Quotes Section */}
                <h2 style={{
                    margin: '0 0 16px 0',
                    padding: '0',
                    color: '#334155',
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    lineHeight: '1.3',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}>Quotes actives</h2>
                {activeQuotes.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: '#f8fafc',
                        border: '2px dashed #e2e8f0',
                        borderRadius: '16px',
                        marginTop: '0',
                        marginBottom: '32px',
                    }}>
                        <p style={{
                            margin: '0',
                            color: '#64748b',
                            fontSize: '1.1rem',
                            fontWeight: '500',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        }}>No hi ha quotes actives</p>
                        {can(userInfo, 'MANAGE_QUOTES') ? (
                            <p style={{
                                marginTop: '8px',
                                fontSize: '0.95rem',
                                color: '#94a3b8',
                                fontStyle: 'italic',
                            }}>Crea la primera quota a la pestanya "Crear nova"</p>
                        ) : null}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%', marginBottom: '32px' }}>
                        {activeQuotes.map(q => renderQuoteCard(q, false))}
                    </div>
                )}

                {/* Past Quotes Section (expired + past years) */}
                {pastQuotes.length > 0 ? (
                    <div style={{ marginBottom: '32px' }}>
                        <h2
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: '0 0 16px 0',
                                padding: '0',
                                color: '#334155',
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                lineHeight: '1.3',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            }}
                            onClick={() => setPastQuotesCollapsed(prev => !prev)}
                        >
                            Quotes passades
                            <span style={{ fontSize: '0.7em', transition: 'transform 0.2s' }}>{pastQuotesCollapsed ? '▶' : '▼'}</span>
                            <span style={{ fontSize: '0.8em', color: '#94a3b8', fontWeight: 'normal' }}>({pastQuotes.length})</span>
                        </h2>
                        {!pastQuotesCollapsed ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0', width: '100%' }}>
                                {pastQuotes.map(q => renderQuoteCard(q, false))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div className="quotes-container treasury-scope">
            <UserInfo castellersInfo={castellersInfo} {...props} socket={socket} userInfo={userInfo} />
            <HeaderTabs userInfo={userInfo} {...props} treasuryWarnings={treasuryWarnings} />
            <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                ← Tornar a la pàgina principal
            </Pressable>

            <h1>Tresoreria</h1>

            <div className="quotes-layout">
                <div className="navigation-tabs" ref={navigationRef} aria-label="Apartats de Tresoreria">
                    <button
                        className={`tab-button ${activeTab === 'quotes' ? 'active' : ''}`}
                        onClick={() => changeTab('quotes')}
                    >
                        <i className="fas fa-list"></i> Quotes existents
                    </button>
                    {can(userInfo, 'MANAGE_QUOTES') && (
                        <button
                            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
                            onClick={() => changeTab('create')}
                        >
                            <i className="fas fa-plus-circle"></i> Crear nova
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'mensuals' ? 'active' : ''}`}
                        onClick={() => changeTab('mensuals')}
                    >
                        <i className="fas fa-sync-alt" aria-hidden="true"></i>
                        Recurrents
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'patrocinadors' ? 'active' : ''}`}
                        onClick={() => changeTab('patrocinadors')}
                    >
                        <i className="fas fa-handshake" aria-hidden="true"></i>
                        Patrocinadors
                    </button>
                    {showAletaBillingTab && can(userInfo, 'ACCESS_ALETA_BILLING') && (
                        <button
                            className={`tab-button ${activeTab === 'factures-aleta' ? 'active' : ''}`}
                            onClick={() => changeTab('factures-aleta')}
                        >
                            <i className="fas fa-file-pdf" aria-hidden="true"></i>
                            Factures Aleta
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => changeTab('info')}
                    >
                        <i
                            className="fas fa-file-invoice"
                            aria-hidden="true"
                        ></i>
                        Dades fiscals
                        {treasuryWarnings.includes('Dades fiscals') && (
                            <span className="tab-badge warning" title="Cal completar les dades fiscals">!</span>
                        )}
                    </button>
                    <button
                        className={`tab-button legal-board-tab-button ${activeTab === 'junta-legal' ? 'active' : ''}`}
                        onClick={() => changeTab('junta-legal')}
                        aria-current={activeTab === 'junta-legal' ? 'page' : undefined}
                    >
                        <i className="fas fa-landmark" aria-hidden="true"></i>
                        Registre
                        {legalBoardBadge?.level ? (
                            <span
                                className={`tab-badge warning legal-board-tab-badge ${legalBoardBadge.level === 'urgent' ? 'legal-board-tab-badge-urgent' : ''}`}
                                title={legalBoardBadge.level === 'urgent' ? 'La junta legal necessita una acció urgent' : 'La junta legal necessita la teva atenció'}
                                aria-label={legalBoardBadge.level === 'urgent' ? 'Acció urgent a Registre' : 'Pendent a Registre'}
                            >
                                !
                            </span>
                        ) : null}
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'utilitat-publica' ? 'active' : ''}`}
                        onClick={() => changeTab('utilitat-publica')}
                        aria-current={activeTab === 'utilitat-publica' ? 'page' : undefined}
                    >
                        <i className="fas fa-hand-holding-heart" aria-hidden="true"></i>
                        Utilitat pública
                        {publicUtilityBadge?.level ? (
                            <span
                                className={`tab-badge warning legal-board-tab-badge ${publicUtilityBadge.level === 'urgent' ? 'legal-board-tab-badge-urgent' : ''}`}
                                title={publicUtilityBadge.level === 'urgent' ? 'Hi ha un termini passat o d’aquesta setmana' : 'S’acosta un termini'}
                                aria-label={publicUtilityBadge.level === 'urgent' ? 'Acció urgent a Utilitat pública' : 'Pendent a Utilitat pública'}
                            >
                                !
                            </span>
                        ) : null}
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'stripe' ? 'active' : ''}`}
                        onClick={() => changeTab('stripe')}
                    >
                        <i className="fas fa-credit-card" aria-hidden="true"></i>
                        Pagaments
                        {hasStripeConnectWarning ? (
                            <span
                                className="tab-badge warning"
                                title={stripeConnectStatus?.verified ? 'Hi ha requisits pendents de Stripe' : 'Cal configurar Stripe Connect'}
                            >
                                !
                            </span>
                        ) : null}
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'payouts' ? 'active' : ''}`}
                        onClick={() => changeTab('payouts')}
                    >
                        <i className="fas fa-university" aria-hidden="true"></i>
                        Payouts
                    </button>
                    {can(userInfo, 'MANAGE_QUOTES') && (
                        <button
                            className={`tab-button ${activeTab === 'notificacions' ? 'active' : ''}`}
                            onClick={() => changeTab('notificacions')}
                        >
                            <i className="fas fa-bell" aria-hidden="true"></i>
                            Notificacions
                        </button>
                    )}
                </div>

                <div className="quotes-content">
                    {renderTabContent()}
                </div>
            </div>

            {/* Quote Payments Modal */}
            {selectedQuotePayments && (
                <TreasuryDialog label="Pagaments de la quota" className="payments-modal" onClose={closePaymentsModal}>
                    <div className="modal-header">
                        <h3>Pagaments de la quota</h3>
                        <p className="treasury-payment-quote-name">{quotes.find(q => q.id === selectedQuotePayments.quoteId)?.titol}</p>
                        <div>
                            {selectedQuotePayments.payments.length > 0 && schedulerReady && (
                                <button
                                    onClick={toggleOcrMode}
                                    disabled={ocrGlobalProcessing}
                                    className="action-button quotes-ocr-button"
                                >
                                    {ocrGlobalProcessing ? '⏳ Processant...' : (ocrReadMode ? '👁️ Amagar Lectura' : '🪄 Llegir Comprovants')}
                                </button>
                            )}
                            {!schedulerReady && selectedQuotePayments.payments.length > 0 && (
                                <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#777' }}>(Preparant lector...)</span>
                            )}
                            <div className="modal-header-stats">
                                {(() => {
                                    const paidNormally = selectedQuotePayments.payments.filter(p => p.pagada).length;
                                    const familyPaid = selectedQuotePayments.payments.filter(p => p.family_paid_by_name).length;
                                    const paid = paidNormally + familyPaid;
                                    const total = selectedQuotePayments.payments.length;
                                    return (
                                        <span className="modal-stats">
                                            {paid} de {total} pagats
                                        </span>
                                    );
                                })()}
                            </div>
                            <button className="close-button" aria-label="Tancar" onClick={closePaymentsModal}>×</button>
                        </div>
                    </div>

                    <div className="payments-list treasury-dialog-body">
                        <div className="payments-filters">
                            <div className="search-container">
                                <input
                                    type="text"
                                    aria-label="Buscar casteller"
                                    placeholder="Buscar casteller..."
                                    value={paymentSearchTerm}
                                    onChange={e => setPaymentSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                                <span className="search-icon">🔍</span>
                            </div>

                            <div className="filter-buttons">
                                <button
                                    className={`filter-button ${paymentFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => setPaymentFilter('all')}
                                >
                                    Tots ({selectedQuotePayments.payments.length})
                                </button>
                                <button
                                    className={`filter-button ${paymentFilter === 'paid' ? 'active' : ''}`}
                                    onClick={() => setPaymentFilter('paid')}
                                >
                                    Pagats ({selectedQuotePayments.payments.filter(p => p.pagada).length})
                                </button>
                                <button
                                    className={`filter-button ${paymentFilter === 'family_paid' ? 'active' : ''}`}
                                    onClick={() => setPaymentFilter('family_paid')}
                                >
                                    Pagat per fam. ({selectedQuotePayments.payments.filter(p => p.family_paid_by_name && !p.pagada).length})
                                </button>
                                <button
                                    className={`filter-button ${paymentFilter === 'pending' ? 'active' : ''}`}
                                    onClick={() => setPaymentFilter('pending')}
                                >
                                    Pendents ({selectedQuotePayments.payments.filter(p => !p.pagada && !p.family_paid_by_name && p.payment_reference).length})
                                </button>
                                <button
                                    className={`filter-button ${paymentFilter === 'not_paid' ? 'active' : ''}`}
                                    onClick={() => setPaymentFilter('not_paid')}
                                >
                                    Sense resposta ({selectedQuotePayments.payments.filter(p => !p.pagada && !p.family_paid_by_name && !p.payment_reference).length})
                                </button>
                            </div>
                        </div>
                        {(() => {
                            const currentQuote = quotes.find(q => q.id === selectedQuotePayments.quoteId);
                            const basePayments = selectedQuotePayments.payments || [];
                            const searchLower = paymentSearchTerm.trim().toLowerCase();
                            const existingCastellerIds = new Set(basePayments.map(p => p.casteller_id));

                            // Include hidden castellers in search results even if they don't have a payment row yet.
                            const hiddenSearchOnlyPayments = searchLower
                                ? Object.values(castellersInfo || {})
                                    .filter(c => c && c.hidden && !existingCastellerIds.has(c.id))
                                    .filter(c => {
                                        const hiddenFullName = `${c.nom || ''} ${c['primer-cognom'] || ''} ${c.mote || ''}`.toLowerCase();
                                        return hiddenFullName.includes(searchLower);
                                    })
                                    .map(c => ({
                                        id: `hidden_notpaid_${c.id}`,
                                        casteller_id: c.id,
                                        nom: c.nom,
                                        cognom: c['primer-cognom'],
                                        mote: c.mote,
                                        pagada: false,
                                        payment_reference: null,
                                        data_pagament: null,
                                        family_paid_by_name: null,
                                        hidden: true
                                    }))
                                : [];

                            const filteredPayments = [...basePayments, ...hiddenSearchOnlyPayments]
                                .filter(p => {
                                    // Apply search filter
                                    if (searchLower) {
                                        const fullName = `${p.nom || ''} ${p.cognom || ''} ${p.mote || ''}`.toLowerCase();
                                        if (!fullName.includes(searchLower)) {
                                            return false;
                                        }
                                    }

                                    // Apply status filter
                                    if (paymentFilter === 'paid') {
                                        return p.pagada;
                                    } else if (paymentFilter === 'pending') {
                                        return !p.pagada && !p.family_paid_by_name && p.payment_reference;
                                    } else if (paymentFilter === 'not_paid') {
                                        return !p.pagada && !p.family_paid_by_name && !p.payment_reference;
                                    } else if (paymentFilter === 'family_paid') {
                                        return p.family_paid_by_name && !p.pagada;
                                    }

                                    return true; // 'all' filter
                                });

                            if (filteredPayments.length === 0) {
                                return (
                                    <div className="empty-filtered-state">
                                        <p>No s'han trobat resultats</p>
                                        {(paymentSearchTerm || paymentFilter !== 'all') && (
                                            <button
                                                className="clear-filters-button"
                                                onClick={() => {
                                                    setPaymentSearchTerm('');
                                                    setPaymentFilter('all');
                                                }}
                                            >
                                                Esborrar filtres
                                            </button>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <TreasuryTable label="Pagaments de la quota">
                                    <table className="treasury-payments-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Casteller</th>
                                                <th scope="col">Estat</th>
                                                <th scope="col">Variant i import</th>
                                                <th scope="col">Comprovant</th>
                                                <th scope="col">Data</th>
                                                <th scope="col">Accions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPayments.map(p => {
                                                const ocrDataForThisReceipt = ocrResults[p.payment_reference];
                                                const hasOcrDataToDisplay = ocrDataForThisReceipt && (ocrDataForThisReceipt.loading || ocrDataForThisReceipt.error || ocrDataForThisReceipt.date || ocrDataForThisReceipt.importValue);

                                                return (
                                                    <tr key={p.id}>
                                                        <th scope="row">
                                                            <div className="casteller-name">
                                                                {getCastellerNameWithMote(p)}
                                                                {p.hidden ? ' [ocult]' : ''}
                                                            </div>
                                                        </th>
                                                        <td>
                                                            <div className="payment-status">
                                                                {p.pagada ? (
                                                                    <span className="status-badge paid">✓ Pagat</span>
                                                                ) : p.family_paid_by_name ? (
                                                                    <span className="status-badge paid-differently">👨‍👩‍👧 Pagat per fam. ({p.family_paid_by_name})</span>
                                                                ) : p.payment_reference ? (
                                                                    <span className="status-badge pending">⏳ Pendent confirmació</span>
                                                                ) : (
                                                                    <span className="status-badge not-paid">Sense resposta</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const variantName = p.variant_nom || (currentQuote ? currentQuote.nom : null);
                                                                const baseImport = p.variant_nom
                                                                    ? parseFloat(p.variant_import || 0)
                                                                    : (currentQuote ? parseFloat(currentQuote.import || 0) : null);
                                                                const adultsText = p.has_adults_addon && p.num_adults >= 1
                                                                    ? ` + ${p.num_adults} adult${p.num_adults > 1 ? 's' : ''} (${formatMoney(parseFloat(p.num_adults || 1) * parseFloat(p.adult_price || 0))}€)`
                                                                    : '';
                                                                const childrenText = p.num_children > 0
                                                                    ? ` + ${p.num_children} infant${p.num_children > 1 ? 's' : ''} (${formatMoney(parseFloat(p.num_children || 0) * parseFloat(p.children_price || 0))}€)`
                                                                    : '';
                                                                const donationAmount = parseFloat(p.donation_amount || 0);
                                                                const donationText = donationAmount > 0
                                                                    ? ` + donatiu (${formatMoney(donationAmount)}€)`
                                                                    : '';

                                                                return variantName ? (
                                                                    <div className="payment-variant-info" style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '4px' }}>
                                                                        📋 {variantName}: {baseImport !== null ? `${formatMoney(baseImport)}€` : ''}
                                                                        {adultsText}{childrenText}{donationText}
                                                                    </div>
                                                                ) : null;
                                                            })()}
                                                        </td>
                                                        <td>
                                                            <div className="quote-receipt-section" style={{ position: 'relative' }}>
                                                                {paymentReceipts[p.payment_reference]?.loading ? (
                                                                    <div className="receipt-loading">Carregant...</div>
                                                                ) : paymentReceipts[p.payment_reference]?.error ? (
                                                                    <div className="receipt-error">Error</div>
                                                                ) : paymentReceipts[p.payment_reference]?.base64 ? (
                                                                    <>
                                                                        <button type="button" className="treasury-receipt-preview" aria-label="Veure comprovant" onClick={() => viewReceipt(p.payment_reference)}>
                                                                            <img
                                                                                className="receipt-thumbnail"
                                                                                src={paymentReceipts[p.payment_reference].base64}
                                                                                alt="Comprovant"
                                                                                style={{
                                                                                    filter: (ocrReadMode && hasOcrDataToDisplay) ? 'blur(3px)' : 'none',
                                                                                    transition: 'filter 0.3s ease-in-out',
                                                                                    display: 'block',
                                                                                    maxWidth: '100px',
                                                                                    maxHeight: '100px',
                                                                                    width: 'auto',
                                                                                    height: 'auto',
                                                                                    objectFit: 'contain',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            />
                                                                        </button>
                                                                        {ocrReadMode && hasOcrDataToDisplay && (
                                                                            <div style={{
                                                                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                                                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                                                                                color: 'black', textAlign: 'center',
                                                                                fontSize: '14px', padding: '5px', boxSizing: 'border-box',
                                                                                borderRadius: '4px',
                                                                                pointerEvents: 'none'
                                                                            }}
                                                                            >
                                                                                {ocrDataForThisReceipt?.loading && <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '2px 6px', borderRadius: '3px' }}>Llegint...</span>}
                                                                                {ocrDataForThisReceipt?.error && <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '2px 6px', borderRadius: '3px', color: 'red' }}>{ocrDataForThisReceipt.error}</span>}
                                                                                {ocrDataForThisReceipt && !ocrDataForThisReceipt.loading && !ocrDataForThisReceipt.error && (
                                                                                    <>
                                                                                        {ocrDataForThisReceipt.date && (
                                                                                            <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                                                                                                <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '3px 6px', borderRadius: '3px' }}>
                                                                                                    {ocrDataForThisReceipt.date}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ocrDataForThisReceipt.importValue && (
                                                                                            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                                                                                <span style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '3px 8px', borderRadius: '3px' }}>
                                                                                                    {ocrDataForThisReceipt.importValue}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <div style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                                                                        Sense comprovant
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {p.data_pagament ? (
                                                                <div className="payment-date">
                                                                    📅 {new Date(p.data_pagament).toLocaleDateString('ca-ES')}
                                                                </div>
                                                            ) : (
                                                                <div className="payment-date">
                                                        &nbsp;
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="payment-actions">
                                                                <button
                                                                    className={`toggle-button ${p.pagada ? 'unpaid' : 'paid'} ${!p.pagada && p.payment_reference ? 'pending' : ''}`}
                                                                    onClick={() => togglePaymentStatus(selectedQuotePayments.quoteId, p.casteller_id, p.pagada)}
                                                                >
                                                                    {p.pagada ? (
                                                                        <>✓ Pagat</>
                                                                    ) : (
                                                                        <>
                                                                            {currentQuote && Number.parseInt(currentQuote.variants_count, 10) > 1
                                                                                ? 'Marcar pagat'
                                                                                : (p.payment_reference ? '+ Demanat' : '+ Pendent')}
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </TreasuryTable>
                            );
                        })()}
                    </div>
                </TreasuryDialog>
            )}

            {adminMarkPaidModal.visible ? (
                <TreasuryDialog label="Marcar com a pagat" className="quotes-admin-mark-paid-modal treasury-dialog-narrow" onClose={closeAdminMarkPaidModal} closeDisabled={adminMarkPaidModal.submitting}>
                    <div className="modal-header">
                        <h3>Marcar com a pagat</h3>
                        <button className="close-button" aria-label="Tancar" onClick={closeAdminMarkPaidModal} disabled={adminMarkPaidModal.submitting}>×</button>
                    </div>

                    <div className="quotes-admin-mark-paid-modal-body treasury-dialog-body">
                        <p className="quotes-admin-mark-paid-modal-intro">
                            Selecciona la variant per a <strong>{adminMarkPaidModal.castellerName || 'aquest casteller'}</strong>.
                        </p>

                        {adminMarkPaidModal.loading ? (
                            <div className="quotes-admin-mark-paid-modal-state">Carregant variants...</div>
                        ) : (
                            <div className="quotes-admin-mark-paid-modal-grid">
                                <div className="quotes-admin-mark-paid-modal-field">
                                    <label htmlFor="quotes-admin-mark-paid-variant">Variant</label>
                                    <select
                                        id="quotes-admin-mark-paid-variant"
                                        className="quotes-admin-mark-paid-modal-input"
                                        value={adminMarkPaidModal.selectedVariantId}
                                        onChange={(e) => setAdminMarkPaidModal(prev => ({
                                            ...prev,
                                            selectedVariantId: e.target.value,
                                            error: null,
                                        }))}
                                        disabled={adminMarkPaidModal.submitting}
                                    >
                                        <option value="">Selecciona una variant</option>
                                        {adminMarkPaidModal.variants.map(variant => (
                                            <option key={variant.id} value={variant.id}>
                                                {variant.nom}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {adminMarkPaidHasAdultsAddon ? (
                                    <div className="quotes-admin-mark-paid-modal-field">
                                        <label htmlFor="quotes-admin-mark-paid-adults">Adults</label>
                                        <input
                                            id="quotes-admin-mark-paid-adults"
                                            type="text"
                                            inputMode="numeric"
                                            className="quotes-admin-mark-paid-modal-input"
                                            value={adminMarkPaidModal.numAdults}
                                            onChange={(e) => setAdminMarkPaidModal(prev => ({
                                                ...prev,
                                                numAdults: sanitizeNumericText(e.target.value),
                                                error: null,
                                            }))}
                                            disabled={adminMarkPaidModal.submitting}
                                        />
                                    </div>
                                ) : null}

                                {adminMarkPaidHasChildrenAddon ? (
                                    <div className="quotes-admin-mark-paid-modal-field">
                                        <label htmlFor="quotes-admin-mark-paid-children">Infants</label>
                                        <input
                                            id="quotes-admin-mark-paid-children"
                                            type="text"
                                            inputMode="numeric"
                                            className="quotes-admin-mark-paid-modal-input"
                                            value={adminMarkPaidModal.numChildren}
                                            onChange={(e) => setAdminMarkPaidModal(prev => ({
                                                ...prev,
                                                numChildren: sanitizeNumericText(e.target.value),
                                                error: null,
                                            }))}
                                            disabled={adminMarkPaidModal.submitting}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {adminMarkPaidIsFamilyVariant ? (
                            <div className="quotes-admin-mark-paid-modal-note">
                                Aquesta variant pot fer que altres membres del grup familiar surtin com a pagats per família.
                            </div>
                        ) : null}

                        {adminMarkPaidModal.error ? (
                            <div className="quotes-admin-mark-paid-modal-error">
                                {adminMarkPaidModal.error}
                            </div>
                        ) : null}

                    </div>
                    <div className="quotes-admin-mark-paid-modal-actions treasury-dialog-footer">
                        <button
                            className="quotes-admin-mark-paid-modal-cancel"
                            onClick={closeAdminMarkPaidModal}
                            disabled={adminMarkPaidModal.submitting}
                        >
                            Cancel·la
                        </button>
                        <button
                            className="quotes-admin-mark-paid-modal-submit"
                            onClick={confirmAdminMarkPaid}
                            disabled={adminMarkPaidModal.loading || adminMarkPaidModal.submitting}
                        >
                            {adminMarkPaidModal.submitting ? 'Guardant...' : 'Marcar com a pagat'}
                        </button>
                    </div>
                </TreasuryDialog>
            ) : null}

            {/* Edit Quote Modal */}
            {editingQuote ? (
                <TreasuryDialog label="Editar quota" className="payments-modal treasury-dialog-narrow" onClose={() => setEditingQuote(null)}>
                    <div className="modal-header">
                        <h3>Editar quota</h3>
                        <button className="close-button" aria-label="Tancar" onClick={() => setEditingQuote(null)}>×</button>
                    </div>

                    <div className="treasury-dialog-body">
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#4a5568' }}>
                                Títol
                            </label>
                            <input
                                type="text"
                                value={editingQuote.titol || ''}
                                onChange={(e) => setEditingQuote({ ...editingQuote, titol: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#4a5568' }}>
                                Data d'obertura (opcional)
                            </label>
                            <input
                                type="datetime-local"
                                value={editingQuote.data_obertura || ''}
                                onChange={(e) => setEditingQuote({ ...editingQuote, data_obertura: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <small style={{ color: '#718096', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                                Si es deixa buida, la quota serà visible immediatament.
                            </small>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#4a5568' }}>
                                Data límit de pagament (opcional)
                            </label>
                            <input
                                type="datetime-local"
                                value={editingQuote.data_limit || ''}
                                onChange={(e) => setEditingQuote({ ...editingQuote, data_limit: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <small style={{ color: '#718096', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                                Un cop passada, els usuaris ja no podran pagar la quota.
                            </small>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={!!editingQuote.voluntaria}
                                    onChange={(e) => setEditingQuote({ ...editingQuote, voluntaria: e.target.checked })}
                                    style={{ width: '18px', height: '18px' }}
                                />
                                <span style={{ fontWeight: '500' }}>Quota voluntària (permet "No pagar")</span>
                            </label>
                        </div>

                        {stripeConnectStatus?.verified ? (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568' }}>
                                    Comissió Targeta (4% + 0,3€)
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="edit_commission_mode"
                                            checked={editingQuote.commission_mode !== 'colla_absorbs'}
                                            onChange={() => setEditingQuote({ ...editingQuote, commission_mode: 'user_pays' })}
                                            style={{ marginTop: '3px' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: '500' }}>L'usuari paga la comissió</div>
                                            <small style={{ color: '#718096', fontSize: '0.8rem' }}>
                                                Import final = quota + comissió
                                            </small>
                                        </div>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="edit_commission_mode"
                                            checked={editingQuote.commission_mode === 'colla_absorbs'}
                                            onChange={() => setEditingQuote({ ...editingQuote, commission_mode: 'colla_absorbs' })}
                                            style={{ marginTop: '3px' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: '500' }}>La colla absorbeix la comissió</div>
                                            <small style={{ color: '#718096', fontSize: '0.8rem' }}>
                                                L'usuari paga només l'import de la quota
                                            </small>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        ) : null}

                        {/* Quote Variants Editor */}
                        <QuoteVariantsEditor
                            quoteId={editingQuote.id}
                            quoteImport={editingQuote.value}
                            onUpdate={() => fetchQuotes(false)}
                        />

                    </div>
                    <div className="treasury-dialog-actions treasury-dialog-footer">
                        <button
                            onClick={updateQuote}
                            style={{
                                flex: 1,
                                padding: '12px 20px',
                                background: '#48bb78',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem'
                            }}
                        >
                            Desar canvis
                        </button>
                        <button
                            onClick={() => setEditingQuote(null)}
                            style={{
                                padding: '12px 20px',
                                background: '#e2e8f0',
                                color: '#4a5568',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '1rem'
                            }}
                        >
                            Cancel·lar
                        </button>
                    </div>
                </TreasuryDialog>
            ) : null}

            {/* Receipt Modal */}
            {receiptModal.visible && (
                <TreasuryDialog label="Comprovant de pagament" className="receipt-modal" onClose={resetReceiptModal}>
                    <div className="modal-header"><h3>Comprovant de pagament</h3><button className="close-button" aria-label="Tancar" onClick={resetReceiptModal}>×</button></div>
                    <div className="treasury-dialog-body treasury-receipt-body">
                        {receiptModal.loading ? (
                            <div style={{ padding: '50px', textAlign: 'center' }}>
                                <p>Carregant comprovant...</p>
                            </div>
                        ) : receiptModal.base64 ? (
                            <>
                                <img src={receiptModal.base64} alt="Comprovant de pagament" />
                            </>
                        ) : null}
                    </div>
                </TreasuryDialog>
            )}

            {/* Quote Reminder Modal */}
            <QuoteReminderModal
                visible={reminderModal.visible}
                onClose={() => setReminderModal({ ...reminderModal, visible: false })}
                quote={reminderModal.quote}
                unpaidCastellers={reminderModal.unpaidCastellers}
                castellersInfo={castellersInfo}
                onSendReminder={sendReminder}
                sendingNotification={sendingNotification}
                getPeriodLabel={getPeriodLabel}
                isSubscription={reminderModal.isSubscription}
            />

            {/* Espai extra per facilitar l'scroll */}
            <div style={{ height: '200px' }}></div>
        </div>
    );
}

export default QuotesApp;
